import React from 'react';
import { FlaskConical, RefreshCw } from 'lucide-react';
import { callFlagOptions } from '../../data/mockContactCallInfo';
import type { CallFlag } from '../../data/mockContactCallInfo';
export interface ContactPrototypeState {
  paymentProblem: boolean;
  overdueExtraPurchase: boolean;
  multipleSubscriptions: boolean;
  spendingUp: boolean;
  hasSkipsPauses: boolean;
  hasCellarDoorVisits: boolean;
}
interface ContactPrototypeStatesProps {
  state: ContactPrototypeState;
  onChange: (state: ContactPrototypeState) => void;
  callFlag: CallFlag;
  onCallFlagChange: (flag: CallFlag) => void;
}
const CALL_FLAG_CYCLE: CallFlag[] = ['none', ...callFlagOptions.map(option => option.value).filter(value => value !== 'none')];
const toggles: {
  key: keyof ContactPrototypeState;
  label: string;
  on: string;
  off: string;
}[] = [{
  key: 'paymentProblem',
  label: 'Payment problem',
  on: 'Expired card',
  off: 'Card OK'
}, {
  key: 'overdueExtraPurchase',
  label: 'Overdue extra purchase',
  on: 'Overdue',
  off: 'On track'
}, {
  key: 'multipleSubscriptions',
  label: 'Multiple subscriptions',
  on: '2 active',
  off: '1 active'
}, {
  key: 'spendingUp',
  label: 'Spending trend',
  on: 'Buying more',
  off: 'Buying less'
}, {
  key: 'hasSkipsPauses',
  label: 'Has skips/pauses',
  on: 'Shows counts',
  off: 'None'
}, {
  key: 'hasCellarDoorVisits',
  label: 'Cellar door visits',
  on: '6 visits',
  off: 'None'
}];

// Demo-only bar for previewing the contact page's data states; not part of the product UI
export function ContactPrototypeStates({
  state,
  onChange,
  callFlag,
  onCallFlagChange
}: ContactPrototypeStatesProps) {
  const callFlagLabel = callFlag === 'none' ? 'None' : callFlagOptions.find(option => option.value === callFlag)?.label ?? 'None';
  const cycleCallFlag = () => {
    const index = CALL_FLAG_CYCLE.indexOf(callFlag);
    onCallFlagChange(CALL_FLAG_CYCLE[(index + 1) % CALL_FLAG_CYCLE.length]);
  };
  return <section aria-label="Prototype states (demo only)" className="mb-6 rounded-lg border-2 border-dashed border-[#CBD5E1] bg-white/60 px-4 py-3" data-id="element-1483" data-mp="37ie36wk4fhtd">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-1484" data-mp="1gb7y6yys49h4">
        <div className="flex items-center gap-2" data-id="element-1485" data-mp="1kdl35l5pc3cu">
          <span className="inline-flex items-center gap-1 rounded bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#475569]" data-id="element-1486" data-mp="1kc1izjgxd85s">
            <FlaskConical className="w-3 h-3" data-id="element-1487" data-mp="1ucak9bprxjs1" />
            Demo
          </span>
          <span className="text-sm font-semibold text-[#0F172A]" data-id="element-1488" data-mp="1zavlnegri9i2">Prototype states</span>
        </div>

        {toggles.map(toggle => {
        const isOn = state[toggle.key];
        return <div key={toggle.key} className="flex items-center gap-2" data-id="element-1489" data-mp="243onnukuj3qg">
              <button type="button" role="switch" aria-checked={isOn} aria-label={toggle.label} onClick={() => onChange({
            ...state,
            [toggle.key]: !isOn
          })} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${isOn ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-1490" data-mp="281vy67opv8t7">
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isOn ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} data-id="element-1491" data-mp="2n4m8f9g3jmhk" />
              </button>
              <span className="text-[13px] text-[#475569]" data-id="element-1492" data-mp="2e73gdl41c7w6">
                {toggle.label}
                <span className="text-[#94A3B8]" data-id="element-1493" data-mp="3augi5i54cuxl"> · {isOn ? toggle.on : toggle.off}</span>
              </span>
            </div>;
      })}

        <div className="flex items-center gap-2" data-id="element-1494" data-mp="35yrnnt7yi485">
          <button type="button" onClick={cycleCallFlag} aria-label={`Call flag: ${callFlagLabel}. Switch to next`} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-[#CBD5E1] bg-white text-[13px] font-medium text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" data-id="element-1495" data-mp="314wwy5txoozk">
            <RefreshCw className="w-3.5 h-3.5 text-[#64748B]" data-id="element-1496" data-mp="3ovaq5zo98tvq" />
            Call flag
          </button>
          <span className="text-[13px] text-[#94A3B8]" data-id="element-1497" data-mp="39chvns1vaope">{callFlagLabel}</span>
        </div>
      </div>
    </section>;
}